import { useUploadedFilesQuery } from "@/hooks/queries/useUploadedFilesQuery";
import { UploadedSessionData, UploadedFileData } from "@/types/common.types";
import {
  Cell,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { ErrorMessage } from "../../common/Error";
import { ShimmerLoader } from "../../common/ShimmerLoader";
import {
  ShimmerTable,
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  StyledTableHead,
  StyledTableHeader,
  StyledTableRow,
} from "../../common/Table";
import Text from "../../common/Text";
import { cn } from "@/lib/utils";

type UploadedStyleTableProps = {
  uploadedSessionData: UploadedSessionData | null;
  onStyleIdClick: (styleId: string) => void;
};

type UploadedStyleHeaderProps = {
  totalStyles: number;
  children: React.ReactNode;
};

const UploadedStyleHeader: React.FC<UploadedStyleHeaderProps> = ({
  children,
  totalStyles,
}) => {
  return (
    <>
      {totalStyles > 0 ? (
        <p className="my-3">
          <Text className="text-text-secondary">
            Showing {totalStyles} Dataset
          </Text>
        </p>
      ) : null}
      {children}
    </>
  );
};

const UploadedJobTable: React.FC<UploadedStyleTableProps> = ({
  uploadedSessionData,
  onStyleIdClick,
}) => {
  const { data, isLoading, isError, error } = useUploadedFilesQuery(
    uploadedSessionData?.id || null,
    {
      enabled: Boolean(uploadedSessionData?.id ?? false),
    }
  );
  const [sorting, setSorting] = useState<SortingState>([]);

  const uploadedStylesColumns: ColumnDef<UploadedFileData>[] = useMemo(
    () => [
      {
        accessorKey: "styleId",
        header: "Style Id",
        cell: ({ row }) => {
          return (
            <div
              className="cursor-pointer capitalize text-blue-600 hover:underline"
              onClick={() => onStyleIdClick(row.original.styleId)}
            >
              {row.original.styleId}
            </div>
          );
        },
      },
    ],
    [onStyleIdClick]
  );

  const table = useReactTable({
    data: data?.data || [],
    columns: uploadedStylesColumns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  });

  const renderCellContent = (cell: Cell<UploadedFileData, unknown>) => {
    const content = flexRender(cell.column.columnDef.cell, cell.getContext());
    return content !== null && content !== undefined ? (
      content
    ) : (
      <ShimmerLoader height={16} width={16} />
    );
  };

  if (isLoading) {
    return (
      <UploadedStyleHeader totalStyles={0}>
        <ShimmerTable<UploadedFileData>
          columns={uploadedStylesColumns}
          rowCount={10}
        />
      </UploadedStyleHeader>
    );
  }

  if (isError) {
    return <ErrorMessage message={error?.message} />;
  }

  return (
    <>
      <UploadedStyleHeader totalStyles={data?.totalElements || 0}>
        <StyledTable>
          <StyledTableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <StyledTableRow key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <StyledTableHead
                    key={header.id}
                    className={cn({
                      "px-4":
                        index === 0 || index === headerGroup.headers.length - 1,
                    })}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </StyledTableHead>
                ))}
              </StyledTableRow>
            ))}
          </StyledTableHeader>
          <StyledTableBody className="border-b-[0.5px] border-[#B4B6BA80/50] text-left align-middle">
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <StyledTableRow
                  key={row.id}
                 
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <StyledTableCell
                      key={cell.id}
                      className={cn({
                        "px-4":
                          index === 0 ||
                          index === row.getVisibleCells().length - 1,
                      })}
                    >
                      {renderCellContent(cell)}
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow>
                <StyledTableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-left"
                >
                  No results.
                </StyledTableCell>
              </StyledTableRow>
            )}
          </StyledTableBody>
        </StyledTable>
      </UploadedStyleHeader>
    </>
  );
};

export default UploadedJobTable;
